import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Anchor } from "lucide-react";
import Waves from "@/components/Waves";

const HERO_IMG =
  "https://images.unsplash.com/photo-1520404508792-8e038a1f4fbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxhbHVtaW51bSUyMGJvYXQlMjB3ZWxkaW5nJTIwb2NlYW58ZW58MHx8fHwxNzg2NjM1OTM5fDA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <div className="overflow-hidden">
      <motion.div
        initial={{ y: reduce ? 0 : "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export default function Hero() {
  const sectionRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, 140]);

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] bg-[#003366] flex flex-col overflow-hidden"
    >
      {/* Background image with navy wash + parallax */}
      <div className="absolute inset-0" data-testid="hero-image-frame">
        <motion.img
          src={HERO_IMG}
          alt="Aluminium boat cutting through open ocean off the WA coast"
          style={{ y }}
          className="absolute inset-0 w-full h-[115%] object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003366] via-[#003366]/75 to-[#003366]/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#003366] via-transparent to-[#003366]/40" />
      </div>

      {/* Vertical side label */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 hidden md:block z-10"
        aria-hidden
      >
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.35em] text-blue-300/70 [writing-mode:vertical-rl]">
          Albany WA — Great Southern
        </span>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28 pb-40">
          <MaskedLine delay={0.15}>
            <p
              data-testid="hero-kicker"
              className="font-sans text-xs font-bold uppercase tracking-[0.3em] text-blue-300 flex items-center gap-2.5"
            >
              <Anchor size={14} strokeWidth={2.5} />
              Aluminium Welding & Fabrication
            </p>
          </MaskedLine>
          <h1 className="font-display font-black uppercase tracking-tighter text-white mt-7 text-5xl sm:text-7xl lg:text-8xl leading-[0.95]">
            <MaskedLine delay={0.3}>
              <span data-testid="hero-headline-line-1">If it’s</span>
            </MaskedLine>
            <MaskedLine delay={0.43}>
              <span
                data-testid="hero-headline-line-2"
                className="text-transparent bg-clip-text bg-gradient-to-r from-[#4da3ff] to-[#007bff]"
              >
                Aluminium,
              </span>
            </MaskedLine>
            <MaskedLine delay={0.56}>
              <span data-testid="hero-headline-line-3">We build it.</span>
            </MaskedLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.75 }}
          >
            <p data-testid="hero-subcopy" className="mt-7 max-w-lg text-base md:text-lg leading-relaxed text-blue-100/85">
              Boat modifications, marine maintenance, toolboxes, canopies, trailers and caravan gear —
              aluminium fabrication welded by hand in Albany, WA.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/quote"
                data-testid="hero-quote-cta"
                className="group inline-flex items-center gap-3 bg-white text-[#003366] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:text-white hover:scale-[1.03] transition-[background-color,color,transform] duration-200"
              >
                Request a Quote
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:border-white hover:bg-white/10 transition-colors duration-200"
              >
                Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Water transition into the page */}
      <div className="relative z-10 mt-auto">
        <Waves front="#ffffff" back="rgba(77,163,255,0.45)" />
      </div>
    </section>
  );
}
